import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Monitor,
  Smartphone,
  Tablet,
  Copy,
  Check,
  Palette,
  Layout,
  Building2,
  Settings2,
  MessageCircle,
  X,
  AlertCircle,
  CheckCircle2,
  Code,
  Edit3,
  Loader2,
  AlignLeft,
  AlignCenter,
  AlignRight,
} from "lucide-react";
import {
  Button,
  Input,
  TextAreaInput,
  Label,
  Toggle,
  RangeSlider,
  Select,
} from "@/design-system/components";
import { PageHeading } from "@/design-system/components/heading/heading";
import { Divider } from "@/design-system/components/divider";
import { colors } from "@/design-system/theme/colors";
import { useTemplate, createWidget, getWidgetById, updateWidget } from "../api";
import type { WidgetConfig } from "../api";
import type { WidgetVersion } from "../constants/widget-assets";
import {
  DEFAULT_BRAND_CONFIG,
  DEFAULT_BEHAVIOR_CONFIG,
  DEFAULT_THEME_STYLES,
  DEFAULT_PLACEMENT_CONFIG,
  DEFAULT_ICON_CONFIG,
  DEFAULT_BADGE_CONFIG,
  DEFAULT_ANIMATION_CONFIG,
  DEFAULT_FLOATING_BADGE_CONFIG,
  SHADOW_STYLE_OPTIONS,
  BUTTON_STYLE_OPTIONS,
  BUTTON_SHAPE_OPTIONS,
  FONT_FAMILY_OPTIONS,
  POSITION_OPTIONS,
  COLOR_PRESETS,
  CLOSE_ICON_OPTIONS,
  FLOATING_BADGE_FONT_SIZE_OPTIONS,
} from "../constants";
import type {
  WidgetBrandConfig,
  WidgetBehaviorConfig,
  WidgetThemeStyles,
  WidgetPlacementConfig,
  WidgetIconConfig,
  WidgetBadgeConfig,
  WidgetAnimationConfig,
  WidgetButtonShape,
  WidgetShadowStyle,
  WidgetButtonStyle,
  WidgetPlacement,
  WidgetGradientDirection,
  WidgetFloatingBadgeConfig,
  WidgetBorderStyle,
  WidgetTextCase,
  WidgetIconAnimation,
  WidgetAnimationType,
} from "../types";
import {
  isValidPhoneNumber,
  phoneInputAllowedChars,
  calculateContrastRatio,
  isWCAGCompliant,
} from "../validations";
import WidgetPreview from "../components/customize/widget-preview";
import ColorPicker from "../components/customize/color-picker";
import SectionHeader from "../components/customize/section-header";
import { GOOGLE_FONT_NAMES } from "./customize/google-fonts";
import type {
  CustomizationTab,
  PreviewDeviceType,
} from "./customize/customize.types";
import { generateWidgetEmbedScript } from "./customize/embed-script";
import { getOptimalFloatingBadgePosition } from "./customize/position-helpers";

export function CustomizePage({
  widgetVersion = "1",
}: { widgetVersion?: WidgetVersion } = {}) {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const editWidgetId = searchParams.get("widgetId");
  const { template, isLoading } = useTemplate(templateId);

  const [activeTab, setActiveTab] = useState<CustomizationTab>("brand");
  const [deviceType, setDeviceType] = useState<PreviewDeviceType>("desktop");
  const [chatOpen, setChatOpen] = useState(true);
  const [isOnlinePreview, setIsOnlinePreview] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showScriptModal, setShowScriptModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Widget API states
  const [widgetId, setWidgetId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showUpdateWidgetDialog, setShowUpdateWidgetDialog] = useState(false);
  const [widgetIdInput, setWidgetIdInput] = useState("");
  const [widgetIdError, setWidgetIdError] = useState("");
  const [isLoadingWidget, setIsLoadingWidget] = useState(false);

  // Form state
  const [themeStyles, setThemeStyles] =
    useState<WidgetThemeStyles>(DEFAULT_THEME_STYLES);
  const [placementConfig, setPlacementConfig] = useState<WidgetPlacementConfig>(
    DEFAULT_PLACEMENT_CONFIG
  );
  const [brandConfig, setBrandConfig] =
    useState<WidgetBrandConfig>(DEFAULT_BRAND_CONFIG);
  const [behaviorConfig, setBehaviorConfig] = useState<WidgetBehaviorConfig>(
    DEFAULT_BEHAVIOR_CONFIG
  );
  const [, setIconConfig] = useState<WidgetIconConfig>(DEFAULT_ICON_CONFIG);
  const [, setBadgeConfig] = useState<WidgetBadgeConfig>(DEFAULT_BADGE_CONFIG);
  const [, setAnimationConfig] = useState<WidgetAnimationConfig>(
    DEFAULT_ANIMATION_CONFIG
  );

  useEffect(() => {
    if (template) {
      setThemeStyles({ ...DEFAULT_THEME_STYLES, ...template.themeStyles });
      setPlacementConfig({
        ...DEFAULT_PLACEMENT_CONFIG,
        ...template.placementConfig,
      });
      setIconConfig({ ...DEFAULT_ICON_CONFIG, ...template.iconConfig });
      setBadgeConfig({ ...DEFAULT_BADGE_CONFIG, ...template.badgeConfig });
      setAnimationConfig({
        ...DEFAULT_ANIMATION_CONFIG,
        ...template.animationConfig,
      });
    }
  }, [template]);

  useEffect(() => {
    setChatOpen(behaviorConfig.openByDefault);
  }, [behaviorConfig.openByDefault]);

  useEffect(() => {
    const fontName = GOOGLE_FONT_NAMES[themeStyles.fontFamily];
    if (!fontName) return;
    const linkId = `notifyy-google-font-${fontName.replace(/\+/g, "-")}`;
    if (document.getElementById(linkId)) return;
    const link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;500;600;700&display=swap`;
    document.head.appendChild(link);
  }, [themeStyles.fontFamily]);

  useEffect(() => {
    if (editWidgetId) {
      loadWidgetById(editWidgetId);
    }
  }, [editWidgetId]);

  const populateFormFromWidget = useCallback((config: WidgetConfig) => {
    setBrandConfig((prev) => ({
      ...prev,
      brandName: config.brandName || prev.brandName,
      brandSubtitle: config.brandSubtitle || prev.brandSubtitle,
      phoneNumber: config.phoneNumber || prev.phoneNumber,
      defaultMessage: config.defaultMessage || prev.defaultMessage,
      widgetCTAText: config.ctaText || prev.widgetCTAText,
      agentName: config.agentName || prev.agentName,
      welcomeMessage: config.welcomeMessage || prev.welcomeMessage,
      onlineStatusText: config.onlineStatusText || prev.onlineStatusText,
      offlineStatusText: config.offlineStatusText || prev.offlineStatusText,
    }));

    setThemeStyles((prev) => ({
      ...prev,
      // Colors
      primaryColor: config.primaryColor || prev.primaryColor,
      secondaryColor: config.secondaryColor || prev.secondaryColor,
      headerColor: config.headerColor || prev.headerColor,
      backgroundColor: config.backgroundColor || prev.backgroundColor,
      textColor: config.textColor || prev.textColor,
      hoverColor: config.hoverColor || prev.hoverColor,
      hoverBackgroundColor:
        config.hoverBackgroundColor || prev.hoverBackgroundColor,
      hoverIconColor: config.hoverIconColor || prev.hoverIconColor,
      badgeColor: config.badgeColor || prev.badgeColor,
      statusOnlineColor: config.statusOnlineColor || prev.statusOnlineColor,
      statusOfflineColor: config.statusOfflineColor || prev.statusOfflineColor,

      // Gradient
      useGradient: config.useGradient ?? prev.useGradient,
      gradientDirection:
        (config.gradientDirection as WidgetGradientDirection) ||
        prev.gradientDirection,
      gradientType:
        (config.gradientType as WidgetThemeStyles["gradientType"]) ||
        prev.gradientType,

      // Shape & Layout
      buttonShape:
        (config.buttonShape as WidgetButtonShape) || prev.buttonShape,
      buttonStyle:
        (config.buttonStyle as WidgetButtonStyle) || prev.buttonStyle,
      borderRadius: config.borderRadius ?? prev.borderRadius,
      shadowStyle:
        (config.shadowStyle as WidgetShadowStyle) || prev.shadowStyle,
      borderStyle:
        (config.borderStyle as WidgetBorderStyle) || prev.borderStyle,
      borderColor: config.borderColor || prev.borderColor,
      borderWidth: config.borderWidth ?? prev.borderWidth,
      iconSize: config.iconSize ?? prev.iconSize,
      iconPadding: config.iconPadding ?? prev.iconPadding,

      // Typography
      fontFamily: config.fontFamily || prev.fontFamily,
      fontWeight: config.fontWeight ?? prev.fontWeight,
      fontSize: config.fontSize ?? prev.fontSize,
      lineHeight: config.lineHeight ?? prev.lineHeight,
      textCase: (config.textCase as WidgetTextCase) || prev.textCase,

      // Icon
      iconType: config.iconType || prev.iconType,
      iconColor: config.iconColor || prev.iconColor,
      iconAnimation:
        (config.iconAnimation as WidgetIconAnimation) || prev.iconAnimation,

      // Close Icon
      closeIconType:
        (config.closeIconType as WidgetThemeStyles["closeIconType"]) ||
        prev.closeIconType,
      closeIconColor: config.closeIconColor || prev.closeIconColor,
      closeIconBackgroundColor:
        config.closeIconBackgroundColor || prev.closeIconBackgroundColor,
      closeIconSize: config.closeIconSize ?? prev.closeIconSize,
    }));

    setPlacementConfig((prev) => ({
      ...prev,
      type: (config.position as WidgetPlacement) || prev.type,
      marginTop: config.marginTop ?? prev.marginTop,
      marginBottom: config.marginBottom ?? prev.marginBottom,
      marginRight: config.marginRight ?? prev.marginRight,
      marginLeft: config.marginLeft ?? prev.marginLeft,
      zIndex: config.zIndex ?? prev.zIndex,
      animationType:
        (config.animationType as WidgetAnimationType) || prev.animationType,
      animationDuration: config.animationDuration ?? prev.animationDuration,
    }));

    setBehaviorConfig((prev) => ({
      ...prev,
      openByDefault: config.openByDefault ?? prev.openByDefault,
      showBadge: config.showBadge ?? prev.showBadge,
      badgeText: config.badgeText || prev.badgeText,
      floatingBadge:
        (config.floatingBadge as WidgetFloatingBadgeConfig) ||
        prev.floatingBadge,
    }));
  }, []);

  // Load widget by ID
  const loadWidgetById = useCallback(
    async (id: string) => {
      setIsLoadingWidget(true);
      setWidgetIdError("");
      try {
        const response = await getWidgetById(id);
        if (response.status && response.data) {
          setWidgetId(response.data.widgetId || id);
          setIsEditMode(true);
          populateFormFromWidget(response.data);
          setShowUpdateWidgetDialog(false);
          setWidgetIdInput("");
        } else {
          setWidgetIdError("Widget not found");
        }
      } catch (err) {
        setWidgetIdError(
          "Failed to load widget. Please check the ID and try again."
        );
      } finally {
        setIsLoadingWidget(false);
      }
    },
    [populateFormFromWidget]
  );

  const buildWidgetPayload = useCallback((): Partial<WidgetConfig> => {
    const floatingBadge =
      behaviorConfig.floatingBadge ?? DEFAULT_FLOATING_BADGE_CONFIG;
    // Use auto-calculated position for saving if user hasn't explicitly changed it
    const effectiveFloatingPosition =
      floatingBadge.position === "left" || !floatingBadge.position
        ? getOptimalFloatingBadgePosition(placementConfig.type)
        : floatingBadge.position;

    return {
      // Brand
      phoneNumber: brandConfig.phoneNumber,
      brandName: brandConfig.brandName,
      brandSubtitle: brandConfig.brandSubtitle,
      defaultMessage: brandConfig.defaultMessage,
      ctaText: brandConfig.widgetCTAText,
      agentName: brandConfig.agentName,
      welcomeMessage: brandConfig.welcomeMessage,
      onlineStatusText: brandConfig.onlineStatusText,
      offlineStatusText: brandConfig.offlineStatusText,

      // Colors
      primaryColor: themeStyles.primaryColor,
      secondaryColor: themeStyles.secondaryColor,
      headerColor: themeStyles.headerColor,
      backgroundColor: themeStyles.backgroundColor,
      textColor: themeStyles.textColor,
      hoverColor: themeStyles.hoverColor,
      hoverBackgroundColor: themeStyles.hoverBackgroundColor,
      hoverIconColor: themeStyles.hoverIconColor,
      badgeColor: themeStyles.badgeColor,
      statusOnlineColor: themeStyles.statusOnlineColor,
      statusOfflineColor: themeStyles.statusOfflineColor,

      // Gradient
      useGradient: themeStyles.useGradient,
      gradientDirection: themeStyles.gradientDirection,
      gradientType: themeStyles.gradientType,

      // Shape & Layout
      buttonShape: themeStyles.buttonShape,
      buttonStyle: themeStyles.buttonStyle,
      borderRadius: themeStyles.borderRadius,
      shadowStyle: themeStyles.shadowStyle,
      borderStyle: themeStyles.borderStyle,
      borderColor: themeStyles.borderColor,
      borderWidth: themeStyles.borderWidth,
      iconSize: themeStyles.iconSize,
      iconPadding: themeStyles.iconPadding,

      // Typography
      fontFamily: themeStyles.fontFamily,
      fontWeight: themeStyles.fontWeight,
      fontSize: themeStyles.fontSize,
      lineHeight: themeStyles.lineHeight,
      textCase: themeStyles.textCase,

      // Icon
      iconType: themeStyles.iconType,
      iconColor: themeStyles.iconColor,
      iconAnimation: themeStyles.iconAnimation,

      // Close Icon
      closeIconType: themeStyles.closeIconType,
      closeIconColor: themeStyles.closeIconColor,
      closeIconBackgroundColor: themeStyles.closeIconBackgroundColor,
      closeIconSize: themeStyles.closeIconSize,

      // Position
      position: placementConfig.type,
      marginTop: placementConfig.marginTop,
      marginBottom: placementConfig.marginBottom,
      marginRight: placementConfig.marginRight,
      marginLeft: placementConfig.marginLeft,
      zIndex: placementConfig.zIndex,
      animationType: placementConfig.animationType,
      animationDuration: placementConfig.animationDuration,

      // Behavior
      openByDefault: behaviorConfig.openByDefault,
      showBadge: behaviorConfig.showBadge,
      badgeText: behaviorConfig.badgeText,

      // Floating Badge with auto-positioned value
      floatingBadge: {
        enabled: floatingBadge.enabled,
        message: floatingBadge.message,
        backgroundColor: floatingBadge.backgroundColor,
        textColor: floatingBadge.textColor,
        fontSize: floatingBadge.fontSize,
        glossyEffect: floatingBadge.glossyEffect,
        borderColor: floatingBadge.borderColor,
        borderWidth: floatingBadge.borderWidth,
        position: effectiveFloatingPosition,
        maxWidth: floatingBadge.maxWidth,
        textAlign: floatingBadge.textAlign,
      },
    };
  }, [brandConfig, themeStyles, placementConfig, behaviorConfig]);

  // Auto-calculate optimal floating badge position based on widget placement
  const autoFloatingBadgePosition = useMemo(() => {
    return getOptimalFloatingBadgePosition(placementConfig.type);
  }, [placementConfig.type]);

  const previewConfig = useMemo((): Partial<WidgetConfig> => {
    const floatingBadge =
      behaviorConfig.floatingBadge ?? DEFAULT_FLOATING_BADGE_CONFIG;
    // Use auto-calculated position if user hasn't explicitly changed it from default
    const effectiveFloatingPosition =
      floatingBadge.position === "left" || !floatingBadge.position
        ? autoFloatingBadgePosition
        : floatingBadge.position;

    return {
      // Brand
      phoneNumber: brandConfig.phoneNumber,
      brandName: brandConfig.brandName,
      brandSubtitle: brandConfig.brandSubtitle,
      defaultMessage: brandConfig.defaultMessage,
      ctaText: brandConfig.widgetCTAText,
      agentName: brandConfig.agentName,
      welcomeMessage: brandConfig.welcomeMessage,
      onlineStatusText: brandConfig.onlineStatusText,
      offlineStatusText: brandConfig.offlineStatusText,

      // Colors
      primaryColor: themeStyles.primaryColor,
      secondaryColor: themeStyles.secondaryColor,
      headerColor: themeStyles.headerColor,
      backgroundColor: themeStyles.backgroundColor,
      textColor: themeStyles.textColor,
      hoverColor: themeStyles.hoverColor,
      hoverBackgroundColor: themeStyles.hoverBackgroundColor,
      hoverIconColor: themeStyles.hoverIconColor,
      badgeColor: themeStyles.badgeColor,
      statusOnlineColor: themeStyles.statusOnlineColor,
      statusOfflineColor: themeStyles.statusOfflineColor,

      // Gradient
      useGradient: themeStyles.useGradient,
      gradientDirection: themeStyles.gradientDirection,
      gradientType: themeStyles.gradientType,

      // Shape & Layout
      buttonShape: themeStyles.buttonShape,
      buttonStyle: themeStyles.buttonStyle,
      borderRadius: themeStyles.borderRadius,
      shadowStyle: themeStyles.shadowStyle,
      borderStyle: themeStyles.borderStyle,
      borderColor: themeStyles.borderColor,
      borderWidth: themeStyles.borderWidth,
      iconSize: themeStyles.iconSize,
      iconPadding: themeStyles.iconPadding,

      // Typography
      fontFamily: themeStyles.fontFamily,
      fontWeight: themeStyles.fontWeight,
      fontSize: themeStyles.fontSize,
      lineHeight: themeStyles.lineHeight,
      textCase: themeStyles.textCase,

      // Icon
      iconType: themeStyles.iconType,
      iconColor: themeStyles.iconColor,
      iconAnimation: themeStyles.iconAnimation,

      // Close Icon
      closeIconType: themeStyles.closeIconType,
      closeIconColor: themeStyles.closeIconColor,
      closeIconBackgroundColor: themeStyles.closeIconBackgroundColor,
      closeIconSize: themeStyles.closeIconSize,

      // Position
      position: placementConfig.type,
      marginTop: placementConfig.marginTop,
      marginBottom: placementConfig.marginBottom,
      marginRight: placementConfig.marginRight,
      marginLeft: placementConfig.marginLeft,
      zIndex: placementConfig.zIndex,
      animationType: placementConfig.animationType,
      animationDuration: placementConfig.animationDuration,

      // Behavior
      openByDefault: behaviorConfig.openByDefault,
      showBadge: behaviorConfig.showBadge,
      badgeText: behaviorConfig.badgeText,

      // Floating Badge with auto-positioned value
      floatingBadge: {
        enabled: floatingBadge.enabled,
        message: floatingBadge.message,
        backgroundColor: floatingBadge.backgroundColor,
        textColor: floatingBadge.textColor,
        fontSize: floatingBadge.fontSize,
        glossyEffect: floatingBadge.glossyEffect,
        borderColor: floatingBadge.borderColor,
        borderWidth: floatingBadge.borderWidth,
        position: effectiveFloatingPosition,
        maxWidth: floatingBadge.maxWidth,
        textAlign: floatingBadge.textAlign,
      },
    };
  }, [
    brandConfig,
    themeStyles,
    placementConfig,
    behaviorConfig,
    autoFloatingBadgePosition,
  ]);

  const handleBack = useCallback(() => {
    navigate("/templates");
  }, [navigate]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!brandConfig.phoneNumber) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^[+\d]+$/.test(brandConfig.phoneNumber)) {
      newErrors.phoneNumber = "Only + and digits are allowed";
    } else if (!brandConfig.phoneNumber.startsWith("+")) {
      newErrors.phoneNumber = "Phone number must start with +";
    } else {
      const digitsOnly = brandConfig.phoneNumber.replace(/\D/g, "");
      if (digitsOnly.length < 6) {
        newErrors.phoneNumber = "Phone number must have at least 6 digits";
      } else if (digitsOnly.length > 19) {
        newErrors.phoneNumber = "Phone number cannot exceed 19 digits";
      } else if (!isValidPhoneNumber(brandConfig.phoneNumber)) {
        newErrors.phoneNumber =
          "Enter valid phone with country code (e.g., +1234567890)";
      }
    }
    if (!brandConfig.brandName || brandConfig.brandName.length < 2) {
      newErrors.brandName = "Brand name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generate widget config JSON
  const generateWidgetConfig = useCallback(() => {
    const floatingBadge =
      behaviorConfig.floatingBadge ?? DEFAULT_FLOATING_BADGE_CONFIG;
    return {
      brand: {
        name: brandConfig.brandName,
        subtitle: brandConfig.brandSubtitle,
        phone: brandConfig.phoneNumber,
        defaultMessage: brandConfig.defaultMessage,
        ctaText: brandConfig.widgetCTAText,
        agentName: brandConfig.agentName,
        welcomeMessage: brandConfig.welcomeMessage,
        onlineText: brandConfig.onlineStatusText,
        offlineText: brandConfig.offlineStatusText,
      },
      theme: {
        primaryColor: themeStyles.primaryColor,
        secondaryColor: themeStyles.secondaryColor,
        useGradient: themeStyles.useGradient,
        gradientDirection: themeStyles.gradientDirection,
        buttonShape: themeStyles.buttonShape,
        buttonStyle: themeStyles.buttonStyle,
        borderRadius: themeStyles.borderRadius,
        shadowStyle: themeStyles.shadowStyle,
        fontFamily: themeStyles.fontFamily,
        closeIconType: themeStyles.closeIconType,
        closeIconColor: themeStyles.closeIconColor,
        closeIconBackgroundColor: themeStyles.closeIconBackgroundColor,
        closeIconSize: themeStyles.closeIconSize,
        hoverBackgroundColor: themeStyles.hoverBackgroundColor,
        hoverIconColor: themeStyles.hoverIconColor,
        borderColor: themeStyles.borderColor,
        borderWidth: themeStyles.borderWidth,
      },
      position: {
        type: placementConfig.type,
        marginBottom: placementConfig.marginBottom,
        marginRight: placementConfig.marginRight,
        marginLeft: placementConfig.marginLeft,
        marginTop: placementConfig.marginTop,
      },
      behavior: {
        openByDefault: behaviorConfig.openByDefault,
        showBadge: behaviorConfig.showBadge,
        badgeText: behaviorConfig.badgeText,
        floatingBadge: {
          enabled: floatingBadge.enabled,
          message: floatingBadge.message,
          backgroundColor: floatingBadge.backgroundColor,
          textColor: floatingBadge.textColor,
          fontSize: floatingBadge.fontSize,
          glossyEffect: floatingBadge.glossyEffect,
          borderColor: floatingBadge.borderColor,
          borderWidth: floatingBadge.borderWidth,
          position: floatingBadge.position,
          maxWidth: floatingBadge.maxWidth,
          textAlign: floatingBadge.textAlign,
        },
      },
    };
  }, [brandConfig, themeStyles, placementConfig, behaviorConfig]);

  // Generate embed script with widgetId
  const generateScript = useCallback(
    (wId?: string) => {
      const id = wId || widgetId;
      return generateWidgetEmbedScript({
        widgetVersion,
        widgetId: id,
        inlineConfig: id ? undefined : generateWidgetConfig(),
      });
    },
    [widgetId, generateWidgetConfig, widgetVersion]
  );

  // Handle create or update widget
  const handleGenerateScript = useCallback(async () => {
    const isValid = validateForm();
    if (!isValid) {
      if (errors.phoneNumber || errors.brandName) {
        setActiveTab("brand");
      }
      return;
    }
    const payload = buildWidgetPayload();
    if (isEditMode && widgetId) {
      setIsUpdating(true);
      try {
        const response = await updateWidget(widgetId, payload);
        if (response.status) {
          setShowScriptModal(true);
        }
      } catch (err) {
        console.error("Failed to update widget:", err);
      } finally {
        setIsUpdating(false);
      }
    } else {
      setIsCreating(true);
      try {
        const response = await createWidget(payload);
        if (response.status && response.data.widgetId) {
          setWidgetId(response.data.widgetId);
          setShowScriptModal(true);
        }
      } catch (err) {
        console.error("Failed to create widget:", err);
      } finally {
        setIsCreating(false);
      }
    }
  }, [errors, isEditMode, widgetId, buildWidgetPayload]);

  // Handle update widget button click
  const handleUpdateWidgetClick = useCallback(() => {
    setShowUpdateWidgetDialog(true);
    setWidgetIdInput("");
    setWidgetIdError("");
  }, []);

  // Handle load widget from dialog
  const handleLoadWidget = useCallback(async () => {
    if (!widgetIdInput.trim()) {
      setWidgetIdError("Please enter a widget ID");
      return;
    }
    await loadWidgetById(widgetIdInput.trim());
  }, [widgetIdInput, loadWidgetById]);

  const handleCopyScript = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(generateScript());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [generateScript]);

  // Update handlers
  const updateThemeStyle = useCallback(
    <K extends keyof WidgetThemeStyles>(
      key: K,
      value: WidgetThemeStyles[K]
    ) => {
      setThemeStyles((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const updatePlacement = useCallback(
    <K extends keyof WidgetPlacementConfig>(
      key: K,
      value: WidgetPlacementConfig[K]
    ) => {
      setPlacementConfig((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const updateBrand = useCallback(
    <K extends keyof WidgetBrandConfig>(
      key: K,
      value: WidgetBrandConfig[K]
    ) => {
      setBrandConfig((prev) => ({ ...prev, [key]: value }));
      if (errors[key]) {
        setErrors((prev) => ({ ...prev, [key]: "" }));
      }
    },
    [errors]
  );

  const updateBehavior = useCallback(
    <K extends keyof WidgetBehaviorConfig>(
      key: K,
      value: WidgetBehaviorConfig[K]
    ) => {
      setBehaviorConfig((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  // Apply color preset - automatically sets all related colors based on theme
  const applyColorPreset = useCallback((preset: keyof typeof COLOR_PRESETS) => {
    const presetColors = COLOR_PRESETS[preset];
    // Determine hover icon color based on theme (white for most, dark for light themes)
    const hoverIconColor = preset === "dark" ? "#FFFFFF" : "#FFFFFF";

    setThemeStyles((prev) => ({
      ...prev,
      // Core colors
      primaryColor: presetColors.primary,
      secondaryColor: presetColors.secondary,
      // Header uses primary color by default
      headerColor: presetColors.primary,
      // Hover effects - use theme's hover color
      hoverColor: presetColors.hover,
      hoverBackgroundColor: presetColors.hover,
      hoverIconColor: hoverIconColor,
    }));
  }, []);

  // Calculate contrast ratio
  const contrastRatio = useMemo(() => {
    return calculateContrastRatio(
      themeStyles.textColor,
      themeStyles.backgroundColor
    );
  }, [themeStyles.textColor, themeStyles.backgroundColor]);

  const isContrastCompliant = useMemo(() => {
    return isWCAGCompliant(
      themeStyles.textColor,
      themeStyles.backgroundColor,
      "AA"
    );
  }, [themeStyles.textColor, themeStyles.backgroundColor]);

  const tabs = [
    { id: "brand" as const, label: "Brand", icon: Building2 },
    { id: "chat" as const, label: "Chat", icon: MessageCircle },
    { id: "appearance" as const, label: "Appearance", icon: Palette },
    { id: "position" as const, label: "Position", icon: Layout },
    { id: "behavior" as const, label: "Behavior", icon: Settings2 },
  ];

  if (isLoading) {
    return (
      <div
        className="h-full"
        style={{ backgroundColor: colors.surfaceLightest }}
      >
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div
              className="h-8 rounded w-48"
              style={{ backgroundColor: colors.surfaceLighter }}
            />
            <div className="grid lg:grid-cols-2 gap-8">
              <div
                className="h-96 rounded-xl"
                style={{ backgroundColor: colors.surfaceLighter }}
              />
              <div
                className="h-96 rounded-xl"
                style={{ backgroundColor: colors.surfaceLighter }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full" style={{ backgroundColor: colors.surfaceLightest }}>
      <div className="max-w-7xl mx-auto px-4 py-5">
        <PageHeading
          title={isEditMode ? `Edit Widget: ${widgetId}` : "Customize Widget"}
          subheading={
            isEditMode
              ? "Update your widget configuration"
              : `Based on ${template?.name}`
          }
          actions={
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="small"
                leftIcon={<ArrowLeft size={16} />}
                onClick={handleBack}
              >
                Back
              </Button>
              {!isEditMode && (
                <Button
                  variant="outline"
                  size="medium"
                  leftIcon={<Edit3 size={16} />}
                  onClick={handleUpdateWidgetClick}
                >
                  Update Widget
                </Button>
              )}
              <Button
                variant="primary"
                size="medium"
                leftIcon={
                  isCreating || isUpdating ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Code size={16} />
                  )
                }
                onClick={handleGenerateScript}
                disabled={isCreating || isUpdating}
              >
                {isCreating
                  ? "Creating..."
                  : isUpdating
                  ? "Updating..."
                  : isEditMode
                  ? "Update & Get Script"
                  : "Get Script"}
              </Button>
            </div>
          }
        />
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left: Settings */}
          <div
            className="rounded-xl p-4 space-y-3"
            style={{
              backgroundColor: colors.white,
              border: `1px solid ${colors.surfaceSubtle}`,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            {/* Tabs */}
            <div
              className="flex gap-1 p-1 rounded-lg"
              style={{ backgroundColor: colors.surfaceLighter }}
            >
              {tabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant={activeTab === tab.id ? "primary" : "ghost"}
                  size="small"
                  leftIcon={<tab.icon size={14} />}
                  className="flex-1"
                  style={{
                    backgroundColor:
                      activeTab === tab.id ? colors.white : "transparent",
                    color:
                      activeTab === tab.id ? colors.primary : colors.textMuted,
                    boxShadow:
                      activeTab === tab.id
                        ? "0 1px 3px rgba(0,0,0,0.1)"
                        : "none",
                  }}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </Button>
              ))}
            </div>

            {/* Tab Content */}
            <div
              className="rounded-lg p-4 max-h-[calc(100vh-280px)] overflow-y-auto"
              style={{
                backgroundColor: colors.surfaceLightest,
                border: `1px solid ${colors.surfaceLight}`,
              }}
            >
              {/* Brand Tab */}
              {activeTab === "brand" && (
                <div className="space-y-3">
                  <SectionHeader title="Brand Information" />

                  <Input
                    label="Brand Name"
                    required
                    placeholder="Your Company"
                    size="medium"
                    value={brandConfig.brandName}
                    onChange={(e) => updateBrand("brandName", e.target.value)}
                    error={errors.brandName}
                  />

                  <Input
                    label="Subtitle"
                    placeholder="Typically replies in minutes"
                    size="medium"
                    value={brandConfig.brandSubtitle}
                    onChange={(e) =>
                      updateBrand("brandSubtitle", e.target.value)
                    }
                  />

                  <Input
                    label="WhatsApp Number"
                    required
                    placeholder="+1234567890"
                    size="medium"
                    value={brandConfig.phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow + and digits
                      if (value === "" || phoneInputAllowedChars.test(value)) {
                        // Ensure + is only at the start
                        const sanitized = value.replace(
                          /\+/g,
                          (match, offset) => (offset === 0 ? match : "")
                        );
                        updateBrand("phoneNumber", sanitized);
                      }
                    }}
                    error={errors.phoneNumber}
                    helperText={
                      errors.phoneNumber
                        ? undefined
                        : "6-19 digits with country code (e.g., +1234567890)"
                    }
                  />

                  <TextAreaInput
                    label="Default Message"
                    placeholder="Hello! I'd like to know more..."
                    rows={2}
                    value={brandConfig.defaultMessage}
                    onChange={(e) =>
                      updateBrand("defaultMessage", e.target.value)
                    }
                  />

                  <Input
                    label="Button Text"
                    placeholder="Start Chat"
                    size="medium"
                    value={brandConfig.widgetCTAText}
                    onChange={(e) =>
                      updateBrand("widgetCTAText", e.target.value)
                    }
                  />
                </div>
              )}

              {/* Chat Tab */}
              {activeTab === "chat" && (
                <div className="space-y-3">
                  <SectionHeader title="Chat Window" />

                  <Input
                    label="Agent Name"
                    placeholder="Support"
                    size="medium"
                    value={brandConfig.agentName}
                    onChange={(e) => updateBrand("agentName", e.target.value)}
                  />

                  <TextAreaInput
                    label="Welcome Message"
                    placeholder="Hi there! How can we help?"
                    rows={2}
                    value={brandConfig.welcomeMessage}
                    onChange={(e) =>
                      updateBrand("welcomeMessage", e.target.value)
                    }
                  />

                  <Divider />

                  <SectionHeader title="Status Text" />

                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Online"
                      placeholder="Online now"
                      size="medium"
                      value={brandConfig.onlineStatusText}
                      onChange={(e) =>
                        updateBrand("onlineStatusText", e.target.value)
                      }
                    />
                    <Input
                      label="Offline"
                      placeholder="We'll reply soon"
                      size="medium"
                      value={brandConfig.offlineStatusText}
                      onChange={(e) =>
                        updateBrand("offlineStatusText", e.target.value)
                      }
                    />
                  </div>

                  {/* Preview Status Toggle */}
                  <div
                    className="flex items-center justify-between p-2.5 rounded-lg"
                    style={{ backgroundColor: colors.surfaceSubtle }}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full"
                        style={{
                          backgroundColor: isOnlinePreview
                            ? "#52C41A"
                            : "#FF4D4F",
                        }}
                      />
                      <span
                        className="text-xs"
                        style={{ color: colors.textMuted }}
                      >
                        Preview: {isOnlinePreview ? "Online" : "Offline"}
                      </span>
                    </div>
                    <Toggle
                      checked={isOnlinePreview}
                      onChange={setIsOnlinePreview}
                      size="small"
                    />
                  </div>
                </div>
              )}

              {/* Appearance Tab */}
              {activeTab === "appearance" && (
                <div className="space-y-3">
                  <SectionHeader
                    title="Color Theme"
                    description="Select a preset or customize colors"
                  />

                  {/* Color Presets */}
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(COLOR_PRESETS).map(([key, preset]) => (
                      <button
                        key={key}
                        className="w-8 h-8 rounded-full border-2 p-0 cursor-pointer transition-all duration-200 hover:scale-110"
                        style={{
                          background: `linear-gradient(135deg, ${preset.primary} 0%, ${preset.secondary} 100%)`,
                          borderColor:
                            themeStyles.primaryColor === preset.primary
                              ? colors.primary
                              : "transparent",
                          boxShadow:
                            themeStyles.primaryColor === preset.primary
                              ? `0 0 0 2px ${colors.primaryExtraLight}`
                              : "none",
                        }}
                        onClick={() =>
                          applyColorPreset(key as keyof typeof COLOR_PRESETS)
                        }
                        title={key.charAt(0).toUpperCase() + key.slice(1)}
                      />
                    ))}
                  </div>

                  {/* Primary & Secondary Colors */}
                  <div className="grid grid-cols-2 gap-3">
                    <ColorPicker
                      label="Primary"
                      value={themeStyles.primaryColor}
                      onChange={(v) => updateThemeStyle("primaryColor", v)}
                    />
                    <ColorPicker
                      label="Secondary"
                      value={themeStyles.secondaryColor}
                      onChange={(v) => updateThemeStyle("secondaryColor", v)}
                    />
                  </div>

                  {/* Header Color */}
                  <ColorPicker
                    label="Header Background"
                    value={themeStyles.headerColor || themeStyles.primaryColor}
                    onChange={(v) => updateThemeStyle("headerColor", v)}
                  />

                  <Toggle
                    label="Enable Gradient"
                    labelPosition="left"
                    checked={themeStyles.useGradient}
                    onChange={(checked) =>
                      updateThemeStyle("useGradient", checked)
                    }
                  />

                  {/* Contrast Check */}
                  <div
                    className="p-2.5 rounded-lg flex items-center gap-2"
                    style={{
                      backgroundColor: isContrastCompliant
                        ? colors.successBgSubtle
                        : colors.warningBgSubtle,
                    }}
                  >
                    {isContrastCompliant ? (
                      <CheckCircle2
                        size={16}
                        style={{ color: colors.success }}
                      />
                    ) : (
                      <AlertCircle
                        size={16}
                        style={{ color: colors.warning }}
                      />
                    )}
                    <span
                      className="text-xs"
                      style={{ color: colors.textDefault }}
                    >
                      Contrast: {contrastRatio.toFixed(1)}:1{" "}
                      {isContrastCompliant ? "(WCAG AA)" : ""}
                    </span>
                  </div>

                  <Divider />

                  <SectionHeader title="Button Style" />

                  <div className="grid grid-cols-4 gap-2">
                    {BUTTON_SHAPE_OPTIONS.map((opt) => (
                      <Button
                        key={opt.value}
                        variant={
                          themeStyles.buttonShape === opt.value
                            ? "primary"
                            : "outline"
                        }
                        size="small"
                        className="text-xs"
                        onClick={() =>
                          updateThemeStyle(
                            "buttonShape",
                            opt.value as WidgetButtonShape
                          )
                        }
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>

                  <div className="grid grid-cols-4 gap-2">
                    {BUTTON_STYLE_OPTIONS.map((opt) => (
                      <Button
                        key={opt.value}
                        variant={
                          themeStyles.buttonStyle === opt.value
                            ? "primary"
                            : "outline"
                        }
                        size="small"
                        className="text-xs"
                        onClick={() =>
                          updateThemeStyle(
                            "buttonStyle",
                            opt.value as WidgetButtonStyle
                          )
                        }
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>

                  <div>
                    <Select
                      label="Shadow"
                      options={SHADOW_STYLE_OPTIONS}
                      value={themeStyles.shadowStyle}
                      onChange={(v) =>
                        updateThemeStyle("shadowStyle", v as WidgetShadowStyle)
                      }
                      size="medium"
                    />
                  </div>

                  <RangeSlider
                    label="Popper Border Radius"
                    value={themeStyles.borderRadius}
                    onChange={(v) => updateThemeStyle("borderRadius", v)}
                    min={0}
                    max={24}
                    step={4}
                    unit="px"
                    showValue
                  />

                  <Divider />

                  <SectionHeader
                    title="Hover Effects"
                    description="Customize button appearance on hover"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <ColorPicker
                      label="Hover Background"
                      value={
                        themeStyles.hoverBackgroundColor ||
                        themeStyles.hoverColor ||
                        "#1EBE5A"
                      }
                      onChange={(v) =>
                        updateThemeStyle("hoverBackgroundColor", v)
                      }
                    />
                    <ColorPicker
                      label="Hover Icon Color"
                      value={themeStyles.hoverIconColor || "#FFFFFF"}
                      onChange={(v) => updateThemeStyle("hoverIconColor", v)}
                    />
                  </div>

                  {/* Reset hover to theme defaults */}
                  <button
                    className="text-xs underline cursor-pointer"
                    style={{ color: colors.primary }}
                    onClick={() => {
                      // Find current theme preset and reset hover colors
                      const currentPreset = Object.entries(COLOR_PRESETS).find(
                        ([, preset]) =>
                          preset.primary === themeStyles.primaryColor
                      );
                      if (currentPreset) {
                        const [, preset] = currentPreset;
                        updateThemeStyle("hoverBackgroundColor", preset.hover);
                        updateThemeStyle("hoverIconColor", "#FFFFFF");
                      }
                    }}
                  >
                    Reset to theme defaults
                  </button>

                  <Divider />

                  <SectionHeader
                    title="Border Settings"
                    description="Customize button border"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <ColorPicker
                      label="Border Color"
                      value={themeStyles.borderColor || "#E5E7EB"}
                      onChange={(v) => updateThemeStyle("borderColor", v)}
                    />
                    <RangeSlider
                      label="Border Width"
                      value={themeStyles.borderWidth || 0}
                      onChange={(v) => updateThemeStyle("borderWidth", v)}
                      min={0}
                      max={5}
                      step={1}
                      unit="px"
                      showValue
                    />
                  </div>

                  <Divider />

                  <SectionHeader
                    title="Close Icon"
                    description="Customize the chat close button"
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Select
                        label="Close Icon Type"
                        options={CLOSE_ICON_OPTIONS}
                        value={themeStyles.closeIconType || "x"}
                        onChange={(v) => updateThemeStyle("closeIconType", v)}
                        size="medium"
                      />
                    </div>
                    <div>
                      <RangeSlider
                        label="Close Icon Size"
                        value={themeStyles.closeIconSize || 20}
                        onChange={(v) => updateThemeStyle("closeIconSize", v)}
                        min={12}
                        max={32}
                        step={2}
                        unit="px"
                        showValue
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <ColorPicker
                      label="Close Icon Color"
                      value={themeStyles.closeIconColor || "#FFFFFF"}
                      onChange={(v) => updateThemeStyle("closeIconColor", v)}
                    />
                    <ColorPicker
                      label="Close Icon Background"
                      value={
                        themeStyles.closeIconBackgroundColor || "transparent"
                      }
                      onChange={(v) =>
                        updateThemeStyle("closeIconBackgroundColor", v)
                      }
                    />
                  </div>

                  <Divider />

                  <SectionHeader title="Typography" />

                  <div>
                    <Select
                      label="Font"
                      options={FONT_FAMILY_OPTIONS}
                      value={themeStyles.fontFamily}
                      onChange={(v) => updateThemeStyle("fontFamily", v)}
                      size="medium"
                    />
                  </div>
                </div>
              )}

              {/* Position Tab */}
              {activeTab === "position" && (
                <div className="space-y-3">
                  <SectionHeader title="Widget Position" />

                  <div className="grid grid-cols-2 gap-2">
                    {POSITION_OPTIONS.slice(0, 4).map((opt) => (
                      <Button
                        key={opt.value}
                        variant={
                          placementConfig.type === opt.value
                            ? "primary"
                            : "outline"
                        }
                        size="medium"
                        className="text-xs"
                        onClick={() =>
                          updatePlacement("type", opt.value as WidgetPlacement)
                        }
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>

                  <Divider />

                  <SectionHeader
                    title="Margins"
                    description="Distance from screen edges"
                  />

                  {placementConfig.type.includes("top") && (
                    <RangeSlider
                      label="Top"
                      value={placementConfig.marginTop || 24}
                      onChange={(v) => updatePlacement("marginTop", v)}
                      min={0}
                      max={100}
                      step={4}
                      unit="px"
                      showValue
                    />
                  )}

                  {placementConfig.type.includes("bottom") && (
                    <RangeSlider
                      label="Bottom"
                      value={placementConfig.marginBottom}
                      onChange={(v) => updatePlacement("marginBottom", v)}
                      min={0}
                      max={100}
                      step={4}
                      unit="px"
                      showValue
                    />
                  )}

                  {placementConfig.type.includes("left") && (
                    <RangeSlider
                      label="Left"
                      value={placementConfig.marginLeft}
                      onChange={(v) => updatePlacement("marginLeft", v)}
                      min={0}
                      max={100}
                      step={4}
                      unit="px"
                      showValue
                    />
                  )}

                  {placementConfig.type.includes("right") && (
                    <RangeSlider
                      label="Right"
                      value={placementConfig.marginRight}
                      onChange={(v) => updatePlacement("marginRight", v)}
                      min={0}
                      max={100}
                      step={4}
                      unit="px"
                      showValue
                    />
                  )}
                </div>
              )}

              {/* Behavior Tab */}
              {activeTab === "behavior" && (
                <div className="space-y-3">
                  <SectionHeader title="Display" />

                  <Toggle
                    label="Auto Open"
                    labelPosition="left"
                    description="Open chat window on page load"
                    checked={behaviorConfig.openByDefault}
                    onChange={(v) => updateBehavior("openByDefault", v)}
                  />

                  <Divider />

                  <SectionHeader title="Notification Badge" />

                  <Toggle
                    label="Show Badge"
                    labelPosition="left"
                    description="Display notification badge on button"
                    checked={behaviorConfig.showBadge}
                    onChange={(v) => updateBehavior("showBadge", v)}
                  />

                  {behaviorConfig.showBadge && (
                    <Input
                      label="Badge Text"
                      placeholder="1"
                      size="medium"
                      value={behaviorConfig.badgeText}
                      onChange={(e) =>
                        updateBehavior("badgeText", e.target.value)
                      }
                    />
                  )}

                  <Divider />

                  <SectionHeader
                    title="Floating Badge (Tooltip)"
                    description="Text bubble shown next to widget button"
                  />

                  <Toggle
                    label="Enable Floating Badge"
                    labelPosition="left"
                    description="Show a text tooltip next to the button"
                    checked={
                      behaviorConfig.floatingBadge?.enabled ??
                      DEFAULT_FLOATING_BADGE_CONFIG.enabled
                    }
                    onChange={(v) =>
                      updateBehavior("floatingBadge", {
                        ...(behaviorConfig.floatingBadge ??
                          DEFAULT_FLOATING_BADGE_CONFIG),
                        enabled: v,
                      })
                    }
                  />

                  {(behaviorConfig.floatingBadge?.enabled ??
                    DEFAULT_FLOATING_BADGE_CONFIG.enabled) && (
                    <>
                      <Input
                        label="Message"
                        placeholder="How can we help you?"
                        size="medium"
                        value={
                          behaviorConfig.floatingBadge?.message ??
                          DEFAULT_FLOATING_BADGE_CONFIG.message
                        }
                        onChange={(e) =>
                          updateBehavior("floatingBadge", {
                            ...(behaviorConfig.floatingBadge ??
                              DEFAULT_FLOATING_BADGE_CONFIG),
                            message: e.target.value,
                          })
                        }
                      />

                      <div className="grid grid-cols-2 gap-3">
                        <ColorPicker
                          label="Background Color"
                          value={
                            behaviorConfig.floatingBadge?.backgroundColor ??
                            DEFAULT_FLOATING_BADGE_CONFIG.backgroundColor
                          }
                          onChange={(v) =>
                            updateBehavior("floatingBadge", {
                              ...(behaviorConfig.floatingBadge ??
                                DEFAULT_FLOATING_BADGE_CONFIG),
                              backgroundColor: v,
                            })
                          }
                        />
                        <ColorPicker
                          label="Text Color"
                          value={
                            behaviorConfig.floatingBadge?.textColor ??
                            DEFAULT_FLOATING_BADGE_CONFIG.textColor
                          }
                          onChange={(v) =>
                            updateBehavior("floatingBadge", {
                              ...(behaviorConfig.floatingBadge ??
                                DEFAULT_FLOATING_BADGE_CONFIG),
                              textColor: v,
                            })
                          }
                        />
                      </div>

                      <div>
                        <Select
                          label="Font Size"
                          options={FLOATING_BADGE_FONT_SIZE_OPTIONS}
                          value={
                            behaviorConfig.floatingBadge?.fontSize ??
                            DEFAULT_FLOATING_BADGE_CONFIG.fontSize
                          }
                          onChange={(v) =>
                            updateBehavior("floatingBadge", {
                              ...(behaviorConfig.floatingBadge ??
                                DEFAULT_FLOATING_BADGE_CONFIG),
                              fontSize: Number(v),
                            })
                          }
                          size="medium"
                        />
                      </div>

                      <Toggle
                        label="Glossy Effect"
                        labelPosition="left"
                        description="Add glossy shadow effect to badge"
                        checked={
                          behaviorConfig.floatingBadge?.glossyEffect ??
                          DEFAULT_FLOATING_BADGE_CONFIG.glossyEffect
                        }
                        onChange={(v) =>
                          updateBehavior("floatingBadge", {
                            ...(behaviorConfig.floatingBadge ??
                              DEFAULT_FLOATING_BADGE_CONFIG),
                            glossyEffect: v,
                          })
                        }
                      />

                      <div className="grid grid-cols-2 gap-3">
                        <ColorPicker
                          label="Border Color"
                          value={
                            behaviorConfig.floatingBadge?.borderColor ??
                            DEFAULT_FLOATING_BADGE_CONFIG.borderColor
                          }
                          onChange={(v) =>
                            updateBehavior("floatingBadge", {
                              ...(behaviorConfig.floatingBadge ??
                                DEFAULT_FLOATING_BADGE_CONFIG),
                              borderColor: v,
                            })
                          }
                        />
                        <RangeSlider
                          label="Border Width"
                          value={
                            behaviorConfig.floatingBadge?.borderWidth ??
                            DEFAULT_FLOATING_BADGE_CONFIG.borderWidth
                          }
                          onChange={(v) =>
                            updateBehavior("floatingBadge", {
                              ...(behaviorConfig.floatingBadge ??
                                DEFAULT_FLOATING_BADGE_CONFIG),
                              borderWidth: v,
                            })
                          }
                          min={0}
                          max={5}
                          step={1}
                          unit="px"
                          showValue
                        />
                      </div>

                      <div>
                        <Label className="mb-2">Position</Label>
                        <div className="flex gap-1 flex-wrap">
                          {[
                            {
                              value: "auto" as const,
                              label: `Auto (${autoFloatingBadgePosition})`,
                            },
                            { value: "left" as const, label: "Left" },
                            { value: "right" as const, label: "Right" },
                            { value: "top" as const, label: "Top" },
                            { value: "bottom" as const, label: "Bottom" },
                          ].map(({ value, label }) => {
                            const currentPosition =
                              behaviorConfig.floatingBadge?.position ?? "left";
                            const isAutoSelected =
                              value === "auto" &&
                              (currentPosition === "left" ||
                                currentPosition === autoFloatingBadgePosition);
                            const isSelected =
                              value === "auto"
                                ? isAutoSelected
                                : currentPosition === value;
                            return (
                              <button
                                key={value}
                                type="button"
                                onClick={() => {
                                  if (value === "auto") {
                                    updateBehavior("floatingBadge", {
                                      ...(behaviorConfig.floatingBadge ??
                                        DEFAULT_FLOATING_BADGE_CONFIG),
                                      position: autoFloatingBadgePosition,
                                    });
                                  } else {
                                    updateBehavior("floatingBadge", {
                                      ...(behaviorConfig.floatingBadge ??
                                        DEFAULT_FLOATING_BADGE_CONFIG),
                                      position: value,
                                    });
                                  }
                                }}
                                className="px-3 py-2 rounded-lg border text-xs font-medium transition-all duration-150 cursor-pointer"
                                style={{
                                  backgroundColor: isSelected
                                    ? colors.primary
                                    : colors.white,
                                  borderColor: isSelected
                                    ? colors.primary
                                    : colors.surfaceSubtle,
                                  color: isSelected
                                    ? colors.white
                                    : colors.textDefault,
                                }}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                        <span
                          className="text-xs mt-1 block"
                          style={{ color: colors.textMuted }}
                        >
                          Auto adjusts based on widget placement
                        </span>
                      </div>

                      <RangeSlider
                        label="Badge Width"
                        value={
                          behaviorConfig.floatingBadge?.maxWidth ??
                          DEFAULT_FLOATING_BADGE_CONFIG.maxWidth
                        }
                        onChange={(v) =>
                          updateBehavior("floatingBadge", {
                            ...(behaviorConfig.floatingBadge ??
                              DEFAULT_FLOATING_BADGE_CONFIG),
                            maxWidth: v,
                          })
                        }
                        min={100}
                        max={400}
                        step={10}
                        unit="px"
                        showValue
                        showMinMax
                      />

                      <div>
                        <Label className="mb-2">Text Alignment</Label>
                        <div className="flex gap-1">
                          {[
                            {
                              value: "left" as const,
                              icon: AlignLeft,
                              label: "Left",
                            },
                            {
                              value: "center" as const,
                              icon: AlignCenter,
                              label: "Center",
                            },
                            {
                              value: "right" as const,
                              icon: AlignRight,
                              label: "Right",
                            },
                          ].map(({ value, icon: Icon, label }) => (
                            <button
                              key={value}
                              type="button"
                              onClick={() =>
                                updateBehavior("floatingBadge", {
                                  ...(behaviorConfig.floatingBadge ??
                                    DEFAULT_FLOATING_BADGE_CONFIG),
                                  textAlign: value,
                                })
                              }
                              className="flex items-center justify-center p-2.5 rounded-lg border transition-all duration-150 cursor-pointer"
                              style={{
                                backgroundColor:
                                  (behaviorConfig.floatingBadge?.textAlign ??
                                    DEFAULT_FLOATING_BADGE_CONFIG.textAlign) ===
                                  value
                                    ? colors.primary
                                    : colors.white,
                                borderColor:
                                  (behaviorConfig.floatingBadge?.textAlign ??
                                    DEFAULT_FLOATING_BADGE_CONFIG.textAlign) ===
                                  value
                                    ? colors.primary
                                    : colors.surfaceSubtle,
                                color:
                                  (behaviorConfig.floatingBadge?.textAlign ??
                                    DEFAULT_FLOATING_BADGE_CONFIG.textAlign) ===
                                  value
                                    ? colors.white
                                    : colors.textDefault,
                              }}
                              title={label}
                            >
                              <Icon size={18} />
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right: Preview */}
          <div className="lg:sticky lg:top-5 h-fit">
            <div
              className="rounded-xl p-4"
              style={{
                backgroundColor: colors.white,
                border: `1px solid ${colors.surfaceSubtle}`,
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <h3
                  className="text-sm font-semibold"
                  style={{ color: colors.textInverse }}
                >
                  Preview
                </h3>
                <div className="flex items-center gap-1">
                  {[
                    { type: "desktop" as const, icon: Monitor },
                    { type: "tablet" as const, icon: Tablet },
                    { type: "mobile" as const, icon: Smartphone },
                  ].map(({ type, icon: Icon }) => (
                    <Button
                      key={type}
                      variant={deviceType === type ? "primary" : "ghost"}
                      size="small"
                      leftIcon={<Icon size={16} />}
                      onClick={() => setDeviceType(type)}
                    />
                  ))}
                </div>
              </div>

              {/* Widget Preview using actual widget-1.js */}
              <WidgetPreview
                config={previewConfig}
                deviceType={deviceType}
                isOpen={chatOpen}
                onToggle={() => setChatOpen((prev) => !prev)}
                widgetVersion={widgetVersion}
              />

              <p
                className="text-center text-xs mt-2"
                style={{ color: colors.textMuted }}
              >
                Click widget to toggle
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Script Modal */}
      {showScriptModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div
            className="w-full max-w-2xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: colors.white }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2
                  className="text-lg font-semibold"
                  style={{ color: colors.textInverse }}
                >
                  {isEditMode ? "Widget Updated!" : "Widget Created!"}
                </h2>
                <p className="text-sm" style={{ color: colors.textMuted }}>
                  Copy and paste this code into your website's HTML
                </p>
              </div>
              <button
                className="p-2 rounded-lg cursor-pointer transition-all hover:bg-gray-100"
                onClick={() => setShowScriptModal(false)}
              >
                <X size={20} color={colors.textMuted} />
              </button>
            </div>

            {/* Widget ID Display */}
            {widgetId && (
              <div
                className="mb-4 p-3 rounded-lg flex items-center justify-between"
                style={{
                  backgroundColor: colors.successBgSubtle,
                  border: `1px solid ${colors.success}`,
                }}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={18} style={{ color: colors.success }} />
                  <div>
                    <p
                      className="text-sm font-medium"
                      style={{ color: colors.textInverse }}
                    >
                      Widget ID
                    </p>
                    <p
                      className="text-xs font-mono"
                      style={{ color: colors.textMuted }}
                    >
                      {widgetId}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="small"
                  leftIcon={<Copy size={14} />}
                  onClick={async () => {
                    await navigator.clipboard.writeText(widgetId);
                  }}
                >
                  Copy ID
                </Button>
              </div>
            )}

            <div
              className="rounded-lg p-4 font-mono text-xs overflow-x-auto"
              style={{
                backgroundColor: colors.surfaceLightest,
                border: `1px solid ${colors.surfaceSubtle}`,
              }}
            >
              <pre
                style={{
                  color: colors.textDefault,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                {generateScript()}
              </pre>
            </div>

            <div className="flex items-center gap-3 mt-4">
              <Button
                variant="primary"
                size="medium"
                leftIcon={copied ? <Check size={16} /> : <Copy size={16} />}
                onClick={handleCopyScript}
                className="flex-1"
              >
                {copied ? "Copied!" : "Copy Code"}
              </Button>
              <Button
                variant="secondary"
                size="medium"
                onClick={() => setShowScriptModal(false)}
              >
                Close
              </Button>
            </div>

            <div
              className="mt-4 p-3 rounded-lg flex items-start gap-2"
              style={{ backgroundColor: colors.infoBgSubtle }}
            >
              <AlertCircle
                size={16}
                color={colors.info}
                className="shrink-0 mt-0.5"
              />
              <div>
                <p
                  className="text-sm font-medium"
                  style={{ color: colors.info }}
                >
                  Important
                </p>
                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                  Add this code just before the closing{" "}
                  <code
                    className="px-1 rounded"
                    style={{ backgroundColor: colors.surfaceLighter }}
                  >
                    &lt;/body&gt;
                  </code>{" "}
                  tag for best performance. Save your Widget ID{" "}
                  <strong>{widgetId}</strong> to update this widget later.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Widget Dialog */}
      {showUpdateWidgetDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div
            className="w-full max-w-md rounded-2xl p-6"
            style={{ backgroundColor: colors.white }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2
                  className="text-lg font-semibold"
                  style={{ color: colors.textInverse }}
                >
                  Update Existing Widget
                </h2>
                <p className="text-sm" style={{ color: colors.textMuted }}>
                  Enter your widget ID to load and edit
                </p>
              </div>
              <button
                className="p-2 rounded-lg cursor-pointer transition-all hover:bg-gray-100"
                onClick={() => setShowUpdateWidgetDialog(false)}
              >
                <X size={20} color={colors.textMuted} />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                label="Widget ID"
                placeholder="e.g., mw3x-kpla"
                size="medium"
                value={widgetIdInput}
                onChange={(e) => {
                  setWidgetIdInput(e.target.value);
                  setWidgetIdError("");
                }}
                error={widgetIdError}
              />

              <div className="flex items-center gap-3">
                <Button
                  variant="primary"
                  size="medium"
                  leftIcon={
                    isLoadingWidget ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Edit3 size={16} />
                    )
                  }
                  onClick={handleLoadWidget}
                  disabled={isLoadingWidget}
                  className="flex-1"
                >
                  {isLoadingWidget ? "Loading..." : "Load Widget"}
                </Button>
                <Button
                  variant="secondary"
                  size="medium"
                  onClick={() => setShowUpdateWidgetDialog(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomizePage;
